import { useEffect, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { RealtimeTicketDto } from "@tcr/contracts";
import { api } from "@/api/client";
import { API_BASE, EP } from "@/api/endpoints";
import { useAuth } from "@/auth/AuthProvider";
import { RealtimeManager } from "./manager";
import { applyRealtimeEvent } from "./integrate";
import { useRealtimeStore } from "./store";

function socketUrl(ticket: string): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}${API_BASE}${EP.realtimeSocket}?ticket=${encodeURIComponent(ticket)}`;
}

/** The app-wide realtime connection (one per tab). */
export const realtime = new RealtimeManager({
  fetchTicket: async () => (await api.post<RealtimeTicketDto>(EP.realtimeTickets, {})).ticket,
  socketUrl,
});

/** Starts the socket while signed in and pipes events into the caches. */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { status } = useAuth();

  useEffect(() => {
    const store = useRealtimeStore.getState();
    let openedBefore = false;
    const offState = realtime.onState((s) => {
      useRealtimeStore.getState().setLink(s);
      if (s === "open") {
        // After a reconnect we may have missed events; refetch what's on screen.
        if (openedBefore) void qc.invalidateQueries({ type: "active" });
        openedBefore = true;
      }
      if (s === "idle") openedBefore = false;
    });
    const offRtt = realtime.onRtt((ms) => useRealtimeStore.getState().setRtt(ms));
    const offMsg = realtime.onMessage((msg) => {
      const st = useRealtimeStore.getState();
      if (msg.type === "event") {
        st.markEvent();
        applyRealtimeEvent(qc, msg);
      } else if (msg.type === "subscribed" && msg.denied.length) {
        st.setDenied(msg.denied);
      } else if (msg.type === "error") {
        st.setError(msg);
      }
    });
    store.setLink(realtime.linkState);
    return () => {
      offState();
      offRtt();
      offMsg();
    };
  }, [qc]);

  useEffect(() => {
    if (status !== "authenticated") return;
    realtime.start();
    return () => realtime.stop();
  }, [status]);

  return <>{children}</>;
}

/** Keep a topic subscribed while the calling component is mounted. */
export function useTopic(topic: string | null | undefined): void {
  useEffect(() => {
    if (!topic) return;
    return realtime.subscribe(topic);
  }, [topic]);
}

export function useLinkState() {
  return useRealtimeStore((s) => s.link);
}
